import { FC, useEffect, useState } from 'react';
import CodeIcon from '@mui/icons-material/Code';
import { Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { IEntityContentField } from '../../../lib/interfaces/Common';
import CodeResult from '../CodeEditor/CodeResult/CodeResult';
import ContentRenderer from '../Content/ContentRenderer';
import ContentTitleWithIcon from '../../Simple/ContentTitle/ContentTitleWithIcon';
import ContentTitleSimple from '../../Simple/ContentTitle/ContentTitleSimple';
import ScssRenderer from './ScssRenderer';
import JSonlyRenderer from './JSonlyRenderer';
import './CodeExample.scss';

type CodeExampleProps = {
	field: IEntityContentField;
};

const sanitizeHtmlText = (html: string) => {
	let converted = html;
	converted = converted.replaceAll('<', '&lt;').replaceAll('>', '&gt;');
	return converted;
};

const resizableHintLsKey = 'disableResizableHint';

const CodeExample: FC<CodeExampleProps> = ({ field }) => {
	const { t } = useTranslation();
	const [showResizableHint, setShowResizableHint] = useState<Boolean>(true);

	const htmlText = field.options.html
		? `<pre><code class="language-html">${sanitizeHtmlText(
				field.options.html,
		  )}</code></pre>`
		: null;
	const htmlRenderer = htmlText ? <ContentRenderer html={htmlText} /> : null;

	const cssText = field.options.css
		? `<pre><code class="language-css">${field.options.css}</code></pre>`
		: null;
	const cssRenderer = cssText ? <ContentRenderer html={cssText} /> : null;

	const jsText = field.options.js
		? `<pre><code class="language-js">${field.options.js}</code></pre>`
		: null;

	const isJsOnlyRenderer = field.content === 'js';

	const getJsRenderer = () => {
		if (!jsText) return null;
		if (isJsOnlyRenderer) {
			return (
				<JSonlyRenderer
					jsCodeExample={jsText}
					js={field.options.js}
					field={field}
				/>
			);
		}
		return <ContentRenderer html={jsText} />;
	};
	const jsRenderer = getJsRenderer();

	const scssText = field.options.scss
		? `<pre class="scss"><code class="language-css">${field.options.scss}</code></pre>`
		: null;
	const scssRenderer = scssText ? (
		<ScssRenderer fieldName={field.name} scssText={scssText} />
	) : null;

	const code: {
		css?: string;
		html?: string;
		js?: string;
		scss?: string;
	} = {};
	if (field.options.css) {
		code.css = field.options.css;
	}
	if (field.options.scss) {
		code.scss = field.options.scss;
	}
	if (field.options.html) {
		code.html = field.options.html;
	}
	if (field.options.js) {
		code.js = field.options.js;
	}

	const leftColumnWidth = field.options.leftColumnWidth || 50;
	const rightColumnWidth = 100 - leftColumnWidth;

	useEffect(() => {
		if (localStorage.getItem(resizableHintLsKey)) {
			setShowResizableHint(false);
		}
	}, []);

	const hideResizableHint = () => {
		localStorage.setItem(resizableHintLsKey, 'true');
		setShowResizableHint(false);
	};

	return (
		<div>
			{field.options.title &&
				(field.options.hideTitleIcon ? (
					<ContentTitleSimple
						title={field.options.title}
						id={field.id}
						className="content-column"
					/>
				) : (
					<ContentTitleWithIcon
						content={field.options.title}
						id={field.id}
						icon={CodeIcon}
						className="content-column"
					/>
				))}
			{field.options.description && (
				<ContentRenderer
					html={field.options.description}
					className="content-column"
				/>
			)}
			{!isJsOnlyRenderer && (
				<div style={{ display: 'flex', gap: '0' }} className="CodeExample">
					<div style={{ width: `${leftColumnWidth}%` }}>
						{htmlRenderer}
						{cssRenderer}
						{scssRenderer}
						{jsRenderer}
					</div>

					<div
						className={field.options.isResizable ? 'resizable' : ''}
						style={{
							width: `${rightColumnWidth}%`,
							margin: '0.5em 0',
							border: '3px solid #272727',
							backgroundColor: '#fff',
							borderRadius: '0 3px 3px 0',
							resize: field.options.isResizable ? 'both' : 'none',
							overflow: field.options.isResizable ? 'scroll' : 'none',
						}}
					>
						<CodeResult
							code={code}
							isHorizontalView
							editorsNumber={1}
							hideIframe={false}
							hideFrame
							iframeHeight={field.options.iframeHeight}
							field={field}
							isCodeExample
						/>
						{field.options.isResizable && (
							<div
								className={`${!showResizableHint ? 'disable-hint' : ''} resizable-hint`}
							>
								{t('resizableHint')}
								<Button
									size="small"
									variant="contained"
									disableElevation
									style={{ margin: '2px', backgroundColor: '#fff', color: '#000' }}
									onClick={hideResizableHint}
								>
									{t('gotIt')}
								</Button>
								<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
									<path
										d="M20.383 9.076a1 1 0 0 0-1.09.217L17 11.586 8.707 3.293a1 1 0 0 0-1.414 0l-4 4a1 1 0 0 0 0 1.414L11.586 17l-2.293 2.293A1 1 0 0 0 10 21h10a1 1 0 0 0 1-1V10a1 1 0 0 0-.617-.924z"
										style={{ fill: '#ff7062' }}
									/>
								</svg>
							</div>
						)}
					</div>
				</div>
			)}
			{isJsOnlyRenderer && <div>{jsRenderer}</div>}
		</div>
	);
};

export default CodeExample;
